import React, { useState } from "react";
import { observer } from "mobx-react";

import { DataTable } from "primereact/datatable";
import { Panel } from "primereact/panel";
import { Ripple } from "primereact/ripple";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

import {
  NotificationNotesModal,
  NotificationReportModal,
  useNotificationsStore,
} from "..";
import { Notification, NotificationPageViewModel } from "api";
import { formatDate } from "utils/helpers";
import { Http } from "utils/Http";
import classNames from "classnames";

export const GlobalNotificationTable: React.FC<NotificationPageViewModel> =
  observer((props) => {
    const { notificationGlobalStore, messageStore, ...store } =
      useNotificationsStore();
    const [selectedNotification, setSelectedNotification] = useState<
      Notification | undefined
    >();

    const panelHeaderTemplate = (options: any) => {
      const toggleIcon = options.collapsed
        ? "pi pi-chevron-down"
        : "pi pi-chevron-up";
      const className = `${options.className} p-jc-start`;
      const titleClassName = `${options.titleClassName} p-pl-1`;
      const buttonClassName = `${options.buttonClassName} p-jc-end`;

      return (
        <>
          <div className={className}>
            <button
              className={options.togglerClassName}
              onClick={options.onTogglerClick}
            >
              <span className={toggleIcon}></span>
              <Ripple />
            </button>
            <span className={titleClassName}>Active</span>
            <div className={buttonClassName}>
              <Button
                icon="pi pi-book"
                iconPos="left"
                className="p-button-sm"
                label="Summary"
                onClick={() => {
                  notificationGlobalStore.showReportModal = true;
                }}
              />
            </div>
          </div>
        </>
      );
    };

    const dateBodyTemplate = (notification: Notification) => {
      return formatDate(notification.notificationDate);
    };

    const civilCaseNumberBodyTemplate = (notification: Notification) => {
      const link = Http.resolveClientUrlWithoutSessionToken(
        `go/case?caseId=${notification.caseID}`
      );

      return (
        <div>
          <a href={link} target="_blank" rel="noopener noreferrer">
            {notification.caseNumber}
          </a>
        </div>
      );
    };

    const statusBodyTemplate = (notification: Notification) => {
      return (
        <span
          className={classNames(
            "notification-status",
            "status-" + notification.status
          )}
        >
          {notification.status}
        </span>
      );
    };

    const notesIconBodyTemplate = (notification: Notification) => {
      return (
        <>
          <Button
            icon="pi pi-file"
            className="p-button-rounded p-button-text"
            onClick={() => {
              displayNotes(notification);
            }}
          />
        </>
      );
    };

    const rowClassName = (notification: Notification) => {
      return { "notification-row-overdue": notification.status === "OVERDUE" };
    };

    const displayNotes = (notification: Notification) => {
      notificationGlobalStore.showNotesModal = true;
      setSelectedNotification(notification);
    };

    return (
      <>
        <NotificationNotesModal notification={selectedNotification} />
        <NotificationReportModal />

        <div className="p-mt-4">
          <Panel headerTemplate={panelHeaderTemplate} toggleable>
            <DataTable
              dataKey="id"
              value={notificationGlobalStore.activeNotifications}
              className="p-datatable-sm p-datatable-striped p-datatable-gridlines"
              emptyMessage="No Notifications Found"
              rowHover
              paginator
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              rowsPerPageOptions={[10, 25, 50, 100]}
              rows={10}
              rowClassName={rowClassName}
              sortField="notificationDate"
              sortOrder={1}
            >
              <Column
                field="notificationDate"
                header="Date"
                sortable
                body={dateBodyTemplate}
                style={{ width: "100px" }}
              />
              <Column
                field="notificationCategoryName"
                header="Category"
                sortable
              />
              <Column
                field="caseNumber"
                header="Case"
                sortable
                body={(rowData: any) => {
                  return civilCaseNumberBodyTemplate(rowData);
                }}
              />
              <Column field="caseCaption" header="Caption" />
              <Column
                field="description"
                header="Notes"
                body={(rowData: any) => {
                  return notesIconBodyTemplate(rowData);
                }}
                style={{ width: "5em", textAlign: "center" }}
              />
              <Column
                field="status"
                header="Status"
                sortable
                body={(rowData: any) => {
                  return statusBodyTemplate(rowData);
                }}
                style={{ width: "100px" }}
              />
            </DataTable>
          </Panel>
        </div>
      </>
    );
  });
